// Sdílené typy se serverem. Drženo synchronizované ručně (žádný codegen).
// Když měníš server/lib/parser/types.ts, projdi i tohle.

export type Vlastnik = {
  name: string;
  address: string | null;
  podil: string | null;
  isCompany: boolean;
  isSjm: boolean;
  isStat: boolean;
};

export type Nemovitost = {
  parcela: string | null;
  obec: string | null;
  obecKod: string | null;
  ku: string | null;
  kuKod: string | null;
  lv: string | null;
  vymeraM2: number | null;
  typParcely: string | null;
  druhPozemku: string | null;
  cp: string | null;
  typStavby: string | null;
  ulice: string | null;
  adresniMisto: string | null;
};

export type ParsedKatastr = {
  zdroj: string;
  parsedAt: string;
  nemovitost: Nemovitost;
  vlastnici: Vlastnik[];
  pravoHospodaritStat: Vlastnik[];
  omezeni: string[];
  jineZapisy: string[];
  ochrana: string[];
  flags: string[];
  dedupKey: string;
  warnings: string[];
  rawText: string;
};

export type UploadResponse = {
  ok: true;
  source?: { filename: string; size: number; mimetype: string };
  parsed: ParsedKatastr;
};

/* ---------------- Domain ---------------- */

export type PropertyStatus =
  | 'unclassified'
  | 'parcel_resolved'
  | 'katastr_loaded'
  | 'ready'
  | 'archived';

export type PropertyListItem = {
  id: string;
  dedupKey: string;
  status: PropertyStatus;
  obec: string | null;
  ku: string | null;
  castObce: string | null;
  ulice: string | null;
  cp: string | null;
  parcela: string | null;
  lv: string | null;
  typ: string | null;
  flags: string[];
  notes: string | null;
  sourcePdfId: string | null;
  photoFileId: string | null;
  gpsLat: number | null;
  gpsLon: number | null;
  gpsCapturedAt: string | null;
  /** ms epoch posledního odeslaného (postedAt) dopisu, null pokud žádný. */
  lastLetterAt: number | null;
  createdAt: string;
  updatedAt: string;
};

export const STATUS_LABELS: Record<PropertyStatus, string> = {
  unclassified: 'Nezatříděné',
  parcel_resolved: 'Parcela určena',
  katastr_loaded: 'Katastr nahraný',
  ready: 'Připraveno k odeslání',
  archived: 'Archivováno',
};

export type Contact = {
  id: string;
  ownerId: string;
  channel: string;
  value: string;
  source: string | null;
  verified: boolean;
  optOut: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
};

export type OwnerWithContacts = {
  id: string;
  name: string;
  address: string | null;
  birthDate: string | null;
  isCompany: boolean;
  ico: string | null;
  notes: string | null;
  share: string | null;
  contacts: Contact[];
  createdAt: string;
  updatedAt: string;
};

export type PropertyPhoto = {
  fileId: string;
  ord: number;
};

export type PropertyDetail = {
  ok: true;
  property: PropertyListItem;
  owners: OwnerWithContacts[];
  photos: PropertyPhoto[];
};

export type PropertyListResponse = {
  ok: true;
  items: PropertyListItem[];
  count: number;
};

export type SaveResponse =
  | {
      status: 'created';
      propertyId: string;
      ownerIds: string[];
      contactIds: string[];
    }
  | {
      error: 'duplicate';
      message: string;
      existingId: string;
    };

/* ---------------- Settings ---------------- */

export type UserInfo = {
  jmeno: string;
  telefon: string;
  email: string;
  misto: string;
  adresa: string;
  firma: string;
  /** Osobní identita pro „casual" dopisy bez hlavičky firmy. */
  osobniJmeno: string;
  osobniTelefon: string;
  osobniEmail: string;
};

/* ---------------- Templates & placeholders ---------------- */

export type PlaceholderDef = {
  key: string;
  label: string;
  description: string;
  source: 'derived' | 'settings' | 'owner' | 'property' | 'contact';
  example: string;
};

export type Template = {
  id: string;
  name: string;
  channel: string;
  subject: string | null;
  body: string;
  isDefault: boolean;
  /** Osobní dopis: bez hlavičky firmy, použije osobní telefon/email. */
  isPersonal: boolean;
  createdAt: string;
  updatedAt: string;
};

export type Suggestion = {
  start: number;
  end: number;
  matched: string;
  placeholderKey: string;
  replacement?: string;
  confidence: 'high' | 'medium' | 'low';
  reason: string;
};

/* ---------------- Mailings ---------------- */

export type CampaignStep = { templateId: string; delayDays: number };

export type Campaign = {
  id: string;
  name: string;
  active: boolean;
  conditions: Record<string, unknown>;
  steps: CampaignStep[];
  createdAt: string;
  updatedAt: string;
};

export type CampaignRunStatus = 'active' | 'paused' | 'done' | 'stopped';

export type CampaignRun = {
  id: string;
  propertyId: string;
  campaignId: string;
  currentStep: number;
  nextSendAt: number | null;
  status: CampaignRunStatus;
  createdAt: string;
  updatedAt: string;
};

export type CampaignRunRow = {
  run: CampaignRun;
  property: { id: string; obec: string | null; parcela: string | null; status: PropertyStatus };
};

export type MailingStatus = 'queued' | 'printed' | 'posted' | 'returned' | 'acknowledged';

export type Mailing = {
  id: string;
  runId: string | null;
  propertyId: string;
  ownerId: string;
  contactId: string | null;
  templateId: string;
  stepIndex: number | null;
  channel: string;
  status: MailingStatus;
  renderedHtml: string | null;
  pdfFileId: string | null;
  queuedAt: string;
  printedAt: string | null;
  postedAt: string | null;
  note: string | null;
};

export type MailingListItem = {
  mailing: Mailing;
  template: { id: string; name: string };
  owner: { id: string; name: string };
};

export type GenerateLetterResponse = {
  ok: true;
  mailingId: string;
  pdfFileId: string;
  pdfBytes: number;
  resolvedText: string;
};

/** Distress flagy z {@link ParsedKatastr.flags} — rozdělené pro UI barvy. */
export const DISTRESS_FLAGS = new Set(['exekuce', 'insolvence', 'konkurs', 'zaloba']);
export const INFO_FLAGS = new Set(['zastava', 'predkupni', 'vecne_bremeno']);

export const FLAG_LABELS: Record<string, string> = {
  exekuce: 'Exekuce',
  insolvence: 'Insolvence / úpadek',
  konkurs: 'Konkurs',
  zaloba: 'Podaná žaloba',
  zastava: 'Zástavní právo',
  predkupni: 'Předkupní právo',
  vecne_bremeno: 'Věcné břemeno',
};
